import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "@/lib/api";
import { CP, CapCard, Kpi, HBar } from "./ui";
import { TrendUp, Lightning, ChartLineUp, Users, Cloud, Gear } from "@phosphor-icons/react";

const DRIVER_ICONS = [TrendUp, Lightning, ChartLineUp, Users, Cloud, Gear];
const BAR_COLORS = [CP.purple, CP.blue, CP.green, CP.amber, CP.sky, "#EC4899", "#8B5CF6", "#14B8A6", "#F97316", "#64748B"];

export default function DemandDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/capability/demand/overview").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm py-10 text-center" style={{ color: CP.sub }}>Loading demand overview…</div>;
  const k = data.kpis;
  const maxCap = Math.max(1, ...data.by_capability.map((c) => c.count));

  return (
    <div className="space-y-4" data-testid="demand-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total Demand" value={k.total_demand.toLocaleString()} delta={k.delta_total} testid="demand-kpi-total" />
        <Kpi label="Open Demand" value={k.open_demand} delta={k.delta_fill} deltaLabel="fill momentum" testid="demand-kpi-open" />
        <Kpi label="Avg Urgency" value={k.avg_urgency} tone={k.avg_urgency === "High" ? CP.red : CP.amber} testid="demand-kpi-urgency" />
        <Kpi label="Fill Rate" value={`${k.fill_rate_pct}%`} tone={CP.green} delta={k.delta_fill} testid="demand-kpi-fill" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CapCard title="Demand by Capability" testid="demand-by-capability">
          <div className="space-y-2.5">
            {data.by_capability.map((c, i) => (
              <HBar key={c.skill} label={c.skill} value={c.count} max={maxCap} color={BAR_COLORS[i % BAR_COLORS.length]} />
            ))}
          </div>
        </CapCard>

        <CapCard title="Demand Trend" subtitle="Total vs filled demand per month" testid="demand-trend">
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={data.trend}>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: CP.sub }} tickFormatter={(m) => m.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: CP.sub }} width={30} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="total" name="Total Demand" stroke={CP.purple} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="filled" name="Filled Demand" stroke={CP.green} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CapCard>

        <CapCard title="Top Drivers" subtitle="What is generating demand" testid="demand-drivers">
          <div className="space-y-2">
            {data.top_drivers.map((d, i) => {
              const Icon = DRIVER_ICONS[i % DRIVER_ICONS.length];
              return (
                <div key={d.driver} className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={{ border: `1px solid ${CP.border}` }}>
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#EFF4FF" }}>
                    <Icon size={16} color={CP.blue} weight="duotone" />
                  </span>
                  <span className="text-sm flex-1" style={{ color: CP.text }}>{d.driver}</span>
                  <span className="text-sm font-bold" style={{ color: CP.navy }}>{d.count}</span>
                </div>
              );
            })}
          </div>
        </CapCard>
      </div>
    </div>
  );
}
